import { BuildingAreaType_Enum, type GetProjectSettingsQuery } from "@/graphql";
import type { BranchType, BuildingProjectType, EmissionFactorType, ModelType } from "@/features/BuildingProject";
import { ProjectTemplate } from "@/features/BuildingProject/utils/projectTemplate";
import { sumBuildingAreas, toBuildingUses } from "@/features/BuildingProject/utils/buildingAreaRows";

import type { ProjectWrapperType } from "../types/ProjectWrapperType";

/**
 * The project wrapper built from Hasura rather than from `GET api/BuildingProject/{id}` (RTLCA-5636).
 *
 * Pure: the shape the wizard steps and the save's payload builders read, nothing else. Rows the project does
 * not have yet fall back to the create template's defaults, so a brand-new project reads the same from either
 * source. `includedPhasesTranslated` and `transportTypes` are left empty for the provider to fill, exactly as it
 * does for the REST read.
 */

type SettingsData = GetProjectSettingsQuery;
type SettingsProject = NonNullable<SettingsData["project"]>;
type SettingsModel = SettingsProject["models"][number];
type SettingsEmissionFactor = NonNullable<NonNullable<SettingsProject["installation"]>["heatSupply"]>;

// Hasura's numeric columns arrive as `any` (strings on some drivers); the DTO wants numbers or null.
const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const toEmissionFactor = (row: SettingsEmissionFactor | null | undefined): EmissionFactorType | null => {
  if (!row) return null;
  return {
    sourceId: row.sourceId ?? null,
    customSourceId: row.customSourceId ?? undefined,
    description: row.description ?? undefined,
    type: row.type,
    value: toNumber(row.value),
  };
};

const toBranch = (model: SettingsModel): BranchType => ({
  id: model.id,
  name: model.name,
  commitId: model.commitId ?? null,
  commitPulledDateTime: model.pulledAt ?? "",
  referencedObjectId: model.referencedObjectId ?? null,
});

const toModel = (model: SettingsModel, streamId: string | undefined): ModelType => ({
  id: model.id,
  name: model.name,
  isActive: model.active,
  commitId: model.commitId ?? undefined,
  commitPulledDateTime: model.pulledAt ?? undefined,
  referencedObjectId: model.referencedObjectId ?? null,
  speckleModelId: model.speckleModelId ?? null,
  streamId,
});

const sumOrNull = (first: number | null, second: number | null): number | null =>
  first === null && second === null ? null : (first ?? 0) + (second ?? 0);

export const toProjectWrapper = (data: SettingsData): ProjectWrapperType | null => {
  const source = data.project;
  if (!source) return null;

  const streamId = source.speckleStreamId ?? undefined;
  const template = ProjectTemplate.emissionCalculationAssumptions;

  // Each building use is a pair of rows; the project's figures are the sums over all of them.
  const aboveGround = sumBuildingAreas(source.buildingAreas, BuildingAreaType_Enum.above_ground);
  const belowGround = sumBuildingAreas(source.buildingAreas, BuildingAreaType_Enum.below_ground);
  const areaAboveGround = aboveGround?.area ?? null;
  const areaBelowGround = belowGround?.area ?? null;

  // By name, because the query does not order them and Postgres has no reason to: the rows are
  // deleted and re-inserted wholesale on every model save (`replaceProjectModels`), so their physical
  // order changes under the reader and the chips would reshuffle after each Save and each reload.
  // `finishWrapper` used to hide that by rebuilding the list active-first; it keeps this one now
  // (RTLCA-5756), so the order has to be decided here. The same comparator `modelSignature` sorts by.
  const models = [...source.models].sort((a, b) => a.name.localeCompare(b.name));

  const calculation = source.calculation;
  const installation = source.installation;
  const operational = source.operational;
  const referenceAreas = source.referenceAreas;

  const project: BuildingProjectType = {
    id: source.id,
    projectName: source.name,
    projectNumber: source.projectNumber ?? "",
    clientName: source.clientName ?? "",
    builder: source.builderName ?? "",
    lcaResponsibleName: source.lcaResponsibleName ?? undefined,
    numberOfPeople: toNumber(source.numberOfPeople),
    buildingTypeEnum: source.buildingTypeId,
    // Largest gross area first on every load; edits never re-sort, only the next load does.
    buildingUses: toBuildingUses(source.buildingAreas, source.buildingTypeId),
    buildingProjectStageEnum: source.projectStatusId ?? null,
    streamId,
    buildingAddress: source.location
      ? {
          street: source.location.street,
          streetNumber: source.location.streetNumber,
          postalCode: source.location.postalCode,
          city: source.location.city,
          country: source.location.country,
        }
      : { ...ProjectTemplate.buildingAddress },
    emissionCalculationAssumptions: {
      calculationType: {
        calculationTypeIndicatorId: calculation?.calculationType.thresholdIndicator.indicatorType.id ?? 0,
        calculationTypeEnum: calculation?.calculationTypeId ?? null,
        considerationPeriod: calculation?.considerationPeriod ?? null,
        extraThresholds: [],
        thresholdLimitWithoutExtra: toNumber(calculation?.thresholdLimit),
        installationThresholdLimit: toNumber(calculation?.installationThresholdLimit),
        useDefaultEmissions: calculation?.useDefaultEmissions ?? false,
      },
      emissionMetaData: {
        yearOfConstruction: source.yearOfConstruction,
        yearOfCommissioning: source.yearOfCommissioning,
        includedPhases: (calculation?.stages ?? []).map((stage) => stage.stageId).sort((left, right) => left - right),
        includedPhasesTranslated: [],
      },
      heatArea: {
        heatedFloorAreaAboveGround: aboveGround?.heatedArea ?? null,
        heatedBasementArea: belowGround?.heatedArea ?? null,
      },
      wastagePercentage: installation ? toNumber(installation.wastagePercentage?.value) : template.wastagePercentage,
      projectEnergyData: {
        constructional: {
          earthMoved: toEmissionFactor(installation?.earthMovedByMachinery),
          electricity: toEmissionFactor(installation?.electricitySupply),
          heat: toEmissionFactor(installation?.heatSupply),
          machineryFuel: toEmissionFactor(installation?.machineryFuel),
        },
        operational: {
          electricity: toEmissionFactor(operational?.electricitySupply),
          heat: toEmissionFactor(operational?.heatSupply),
        },
        operationExportedElectricity: toNumber(operational?.exportedElectricity?.value),
        validatedBy: operational?.validatedBy ?? "",
        validationDate: operational?.validatedAt ?? null,
      },
      referencedAreas: {
        values: Object.fromEntries(referenceAreas.map((row) => [row.referenceAreaTypeKey, toNumber(row.area)])),
        entries: referenceAreas.map((row) => ({
          type: row.referenceAreaTypeKey,
          area: toNumber(row.area),
          description: row.description ?? null,
        })),
        basementFloors: belowGround?.floors ?? null,
        floorsAboveGround: aboveGround?.floors ?? null,
        areaAboveGround,
        areaBelowGround,
        totalArea: sumOrNull(areaAboveGround, areaBelowGround),
      },
    },
    transportTypes: [],
    speckleData: {
      branches: models.filter((model) => model.active).map(toBranch),
      inactiveBranches: models.filter((model) => !model.active).map(toBranch),
      streamId,
    },
    modelNames: models.map((model) => model.name),
    speckleConfiguration: {
      parameters: {
        classificationTypeId: data.speckleParameterConfig?.classificationTypeId ?? "",
        classificationCode: data.speckleParameterConfig?.classificationCode ?? "",
      },
    },
    specialConditionConfig: { enabled: source.specialConditionConfig?.enabled ?? false },
  };

  return { project, models: models.map((model) => toModel(model, streamId)) };
};

export default toProjectWrapper;
